import { Box, Typography } from '@mui/material';

import { Card, Section, StatusChip } from './Card';
import {
  CONSISTENCY_LABELS,
  INTENSITY_LABELS,
  LOAD_CHANGE_CAP_PCT,
  TRAINING_DAYS_GUIDELINE,
} from './constants';
import { describeChange, formatNumber, isNum } from './format';

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, fontSize: 15 }}>
    <Box component="dt" sx={{ opacity: 0.8 }}>
      {label}
    </Box>
    <Box component="dd" sx={{ m: 0, fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Box>
  </Box>
);

const percentText = (value) => (isNum(value) ? `${formatNumber(value)}%` : '--');

const loadChangeText = (value) => {
  if (!isNum(value)) {
    return '--';
  }
  if (value > LOAD_CHANGE_CAP_PCT) {
    return `▲ More than ${LOAD_CHANGE_CAP_PCT}% above`;
  }
  const { arrow, text } = describeChange(value);
  return `${arrow} ${text}`.trim();
};

const ConsistencyIntensity = ({ consistency, intensity }) => {
  const c = consistency || {};
  const i = intensity || {};
  return (
    <Section title="Consistency and intensity" id="perf-consistency">
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', tl: 'repeat(2, 1fr)' } }}
      >
        <Card>
          <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
            Consistency
          </Typography>
          <StatusChip
            label={CONSISTENCY_LABELS[c.status] || CONSISTENCY_LABELS.insufficient}
            tone={
              c.status === 'strong' || c.status === 'ok'
                ? 'good'
                : c.status === 'low'
                ? 'caution'
                : 'neutral'
            }
          />
          <Box component="dl" sx={{ m: 0, mt: 1 }}>
            <Row
              label="Training days per week"
              value={
                isNum(c.avgTrainingDaysPerWeek)
                  ? `${formatNumber(
                      c.avgTrainingDaysPerWeek
                    )} (guideline: ${TRAINING_DAYS_GUIDELINE}+)`
                  : '--'
              }
            />
            <Row
              label="Weeks meeting the guideline"
              value={
                isNum(c.weeksMeetingGuideline)
                  ? `${c.weeksMeetingGuideline} of ${c.weeksInWindow}`
                  : '--'
              }
            />
            {isNum(c.activeWeeksInWindow) && (
              <Row
                label="Active weeks"
                value={`${c.activeWeeksInWindow} of ${c.weeksInWindow ?? '--'}`}
              />
            )}
            {isNum(c.awayWeeks) && (
              <Row
                label="Away weeks (not counted)"
                value={`${c.awayWeeks} ${c.awayWeeks === 1 ? 'week' : 'weeks'}`}
              />
            )}
            <Row
              label="Current week streak"
              value={isNum(c.currentWeekStreak) ? `${c.currentWeekStreak} weeks` : '--'}
            />
          </Box>
        </Card>
        <Card>
          <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
            Intensity
          </Typography>
          <StatusChip
            label={INTENSITY_LABELS[i.status] || INTENSITY_LABELS.insufficient}
            tone={
              i.status === 'balanced' ? 'good' : i.status === 'insufficient' ? 'neutral' : 'caution'
            }
          />
          <Box component="dl" sx={{ m: 0, mt: 1 }}>
            <Row label="Heavy share of sets" value={percentText(i.heavySharePct)} />
            <Row
              label="Median reps per set"
              value={isNum(i.medianRepsPerSet) ? i.medianRepsPerSet : '--'}
            />
            <Row label="Weekly load change" value={loadChangeText(i.weeklyLoadChangePct)} />
          </Box>
          {i.loadSpike && (
            <Typography role="note" sx={{ mt: 1, fontSize: 14 }}>
              Your volume jumped this week compared with recent weeks. Add sets gradually over the
              next few sessions rather than all at once.
            </Typography>
          )}
        </Card>
      </Box>
    </Section>
  );
};

export default ConsistencyIntensity;
